/**
 * AuditLogPage — 操作日志（settings 域「操作日志」分区，从 SettingsPage 拆出）
 *
 * 所有运营操作的审计记录（GET /audit-logs，offset 分页）：服务端下推 q / 类型(action[]) /
 * 操作员(actor_id[]) / 日期范围筛选，行展开看字段级 diff + 元数据，支持导出 CSV。
 */

import { isAnonymousRiskAudit } from '@novel-admin/shared';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { useAppScope } from '~/app/app-scope-store';
import { useAuditLogFacets, useAuditLogsPage } from '~/data/queries';
import { auditLogsApi } from '~/hooks/use-admin-users';
import {
  AUDIT_TYPE_OPTIONS,
  type AuditActionType,
  auditTypeChip,
  humanizeAuditAction,
} from '~/lib/audit-action-meta';
import {
  AuditDiffTable,
  auditChangeSummary,
  auditOperationContext,
  auditOperationTimeLabel,
  deriveAuditDiff,
} from '~/lib/audit-display';
import { fmtDateTimeSec, maskIp } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { Avatar } from '~/ui/Avatar';
import { DateRangeButton } from '~/ui/DatePicker';
import { FilterButton } from '~/ui/FilterButton';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { toast } from '~/ui/Toast';
import { SectionHead } from './settings-shared';

export default function AuditLogPage() {
  const [page, setPage] = useState(1);
  const pageSize = useAppScope((s) => s.tablePageSize);
  const [q, setQ] = useState('');
  const [actorFilter, setActorFilter] = useState<string[]>([]); // 操作员（actor_id）多选 → 服务端 actor_id[] 下推
  const [typeFilter, setTypeFilter] = useState<AuditActionType[]>([]); // 类型（动作类型）多选 → 展开为 action[] 服务端下推
  const [range, setRange] = useState<[Date, Date] | null>(null); // 日期范围（created_at）→ 服务端 from/to
  const [expanded, setExpanded] = useState<string | null>(null); // 展开的日志行 id

  // 筛选下拉取全量 facet（GET /audit-logs/facets，按角色收敛）；facet 未就绪/为空时回退当前页去重（不崩）。
  const facetsQuery = useAuditLogFacets();
  const facets = facetsQuery.data;

  // 类型筛选 → action[] 展开：把选中的动作「类型」映射回全量 action code（按 facets.actions 逐个 humanize 判类型）。
  // 选了类型但当前无任何 action 命中 → 下推哨兵 ['__no_match__'] 让后端返回空（语义=该类型本期无记录），而非退化成全量。
  const selectedActions = typeFilter.length
    ? facets.actions.filter((a) => typeFilter.includes(humanizeAuditAction(a).type))
    : [];
  // facet 竞态守门：facets.actions 未就绪（查询未返 / 回退空）时**不下推** action[]——否则会把 ['__no_match__']
  // 发出去让有数据的列表错误空掉。等 facet ready 再按类型展开（与 actor facet 回退当前页去重的兜底对称）。
  const actionFilterParam =
    typeFilter.length && !facetsQuery.isLoading && !facetsQuery.isError
      ? selectedActions.length > 0
        ? selectedActions
        : ['__no_match__']
      : undefined;

  // 服务端下推筛选：q / action[]（由类型展开）/ actor_id[] / from-to 全量数据集过滤，不再单页客户端 .filter()。
  // 日期范围视为「含整日」闭区间 → to 取范围末日 +1 天的右开上界（created_at < to）；ISO 8601 带偏移（UTC Z）。
  const auditFilters = {
    // q.trim()：空白串前端即视为「无筛选」，避免发出后端 .trim().min(1) 会 422 的纯空白 q（与 BooksPage 一致）
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(actionFilterParam ? { action: actionFilterParam } : {}),
    ...(actorFilter.length ? { actor_id: actorFilter } : {}),
    ...(range
      ? {
          from: range[0].toISOString(),
          to: new Date(range[1].getTime() + 86400000).toISOString(),
        }
      : {}),
  };

  // offset 分页（cursor→offset）：page 直接下推，total 来自 envelope 真实总数 → 跳任意页。
  const {
    data: events,
    isLoading,
    isFetching,
    isError,
    isServiceUpdating,
    refetch,
    total: auditTotal,
  } = useAuditLogsPage({
    page,
    limit: pageSize,
    filters: auditFilters,
  });

  // 本地分页偏好变化 → 回第一页（offset 下页码与 pageSize 绑定，换档须重算）。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 该 effect 专门在 pageSize 变化时重置页码。
  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  // 越界页 clamp：filter 收窄后停在旧页码 → 后端返 data=[] 但 total>0。加载完成后 clamp 回末页重取，不卡空表。
  useEffect(() => {
    if (isLoading || isError || facetsQuery.isError) return;
    const maxPage = Math.max(1, Math.ceil(auditTotal / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [auditTotal, pageSize, page, isLoading, isError, facetsQuery.isError]);

  // 改 q / action / 日期范围这些服务端筛选维度时，页码重置回第 1 页（避免在旧页码越界到空页）。
  const resetToFirstPage = () => setPage(1);

  const changeAuditPage = (nextPage: number) => setPage(nextPage);

  // 操作日志 CSV 导出（裸 CSV 流，按角色收敛同 list）：透传整个 auditFilters（q / action[] / actor_id[] / from-to）。
  // actor_id 已随 auditFilters 服务端下推（契约改多值 inArray）→ export 与 list 同源、同筛。
  const exportMutation = useMutation({
    mutationFn: () => auditLogsApi.exportCsv(auditFilters),
    onSuccess: ({ truncated }) =>
      toast(truncated ? '已导出（超上限仅导最新部分）' : '操作日志已导出', {
        tone: truncated ? 'warn' : 'pos',
        icon: 'IconDownload',
      }),
    onError: (err) =>
      toast(
        err instanceof AdminApiError && err.httpStatus === 404
          ? '服务更新中'
          : err instanceof Error
            ? err.message
            : '导出失败',
        { tone: 'neg', icon: 'IconAlert' },
      ),
  });

  // 操作员下拉全量选项（facet，按角色收敛）；facet 未就绪/为空时回退当前页去重（不崩）。
  const actorOptions =
    facets.actors.length > 0
      ? facets.actors.map((a) => ({ value: a.id, label: a.nickname ?? a.id.slice(0, 8) }))
      : Array.from(new Set(events.flatMap((e) => (e.actor_id === null ? [] : [e.actor_id]))))
          .sort()
          .map((id) => ({ value: id, label: id.slice(0, 8) }));

  // 操作员（actor_id）筛选已服务端下推（actor_id[] inArray），不再单页客户端再筛 → 直接消费当前页。
  const updating =
    isServiceUpdating ||
    facetsQuery.isServiceUpdating ||
    (exportMutation.error instanceof AdminApiError && exportMutation.error.httpStatus === 404);
  const readFailed = isError || facetsQuery.isError || exportMutation.isError;
  const reading = isLoading || isFetching || facetsQuery.isLoading || facetsQuery.isFetching;
  const unavailable = updating || readFailed || reading;
  const pageItems = unavailable ? [] : events;
  const readMessage = updating ? '服务更新中' : readFailed ? '操作日志加载失败' : '加载中…';

  return (
    <>
      <SectionHead
        title="操作日志"
        sub={
          unavailable
            ? readMessage
            : `所有运营操作的审计记录 · 保留 18 个月 · 共 ${auditTotal.toLocaleString()} 条`
        }
        action={
          // 导出 CSV：GET /api/v2/audit-logs/export（按角色收敛，透传当前服务端筛选）→ downloadAuthedCsv
          <button
            className="btn"
            disabled={exportMutation.isPending || unavailable}
            onClick={() => {
              if (!unavailable) exportMutation.mutate();
            }}
          >
            <Icons.IconDownload size={12} />
            {exportMutation.isPending ? '导出中…' : '导出 CSV'}
          </button>
        }
      />
      <div className="hstack" style={{ gap: 8 }}>
        <SearchInput
          width={280}
          placeholder="按动作 / 资源搜索…"
          value={q}
          onChange={(e) => {
            // q 服务端下推 → 改 cursor scope，必须重置回第 1 页
            setQ(e.target.value);
            resetToFirstPage();
          }}
        />
        <FilterButton
          label="操作员"
          value={actorFilter}
          onChange={(v) => {
            // actor_id[] 服务端下推（inArray）→ 跨全量数据集过滤，必须重置回第 1 页
            setActorFilter(v);
            resetToFirstPage();
          }}
          options={actorOptions}
          searchable
          width={200}
          emptyLabel="暂无操作员"
        />
        <FilterButton
          label="类型"
          value={typeFilter}
          onChange={(v) => {
            // 类型多选 → 展开为 action[] 服务端下推，必须重置回第 1 页
            setTypeFilter(v as AuditActionType[]);
            resetToFirstPage();
          }}
          options={AUDIT_TYPE_OPTIONS}
          width={160}
          emptyLabel="暂无类型"
        />
        <DateRangeButton
          label={range ? undefined : '日期范围'}
          value={range ?? undefined}
          onChange={(r) => {
            // from/to 服务端下推 → 改 cursor scope，必须重置回第 1 页
            setRange(r);
            resetToFirstPage();
          }}
        />
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        {pageItems.map((e, i) => {
          const isOpen = expanded === e.id;
          // before/after 现成（DTO 已透传）→ 前端按 key 并集 diff；空数组 = 无字段级变更
          const diff = deriveAuditDiff(e.before, e.after);
          const hasDiff = diff.length > 0;
          // 展示派生：操作员名（DTO actor_name；缺=被删账号→回退短 id）/ 人性化动作 + 类型 / 对象名 + 变更摘要
          const anonymous = isAnonymousRiskAudit(e);
          const actorId = e.actor_id;
          const who = anonymous
            ? '系统风控'
            : (e.actor_name ?? e.actor_id?.slice(0, 8) ?? '未知操作员');
          const isSystem = anonymous || e.actor_name === 'system';
          const meta = humanizeAuditAction(e.action);
          const target =
            e.target_name ?? (e.resource_id ? e.resource_id.slice(0, 8) : e.resource ? '' : '—');
          const summary = auditChangeSummary(diff, e.field_refs);
          return (
            <div
              key={e.id}
              style={{
                borderBottom: i < pageItems.length - 1 ? '1px solid var(--border)' : 0,
              }}
            >
              {/* 行头：点击展开/收起 */}
              <div
                className="hstack"
                style={{
                  padding: '10px 14px',
                  gap: 12,
                  cursor: 'pointer',
                  background: isOpen ? 'var(--bg-subtle)' : 'transparent',
                }}
                onClick={() => setExpanded(isOpen ? null : e.id)}
              >
                {/* 头像：system=灰底盾牌；普通操作员按「名字」首字符上色取首字母（修复按 UUID 上色致全同色） */}
                {isSystem ? (
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 999,
                      flexShrink: 0,
                      background: 'var(--bg-inset)',
                      color: 'var(--fg-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icons.IconShield size={13} />
                  </div>
                ) : (
                  <Avatar hue={(who.charCodeAt(0) * 39) % 360} size={30} fontSize={11}>
                    {who[0]?.toUpperCase()}
                  </Avatar>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* 主文案：操作员名 · 人性化动作 对象名（+ 单标量变更摘要） */}
                  <div style={{ fontSize: 12.5 }}>
                    <span style={{ fontWeight: 500 }}>{who}</span>
                    <span style={{ color: 'var(--fg-muted)' }}> · {meta.label} </span>
                    {target && <span style={{ color: 'var(--fg)' }}>{target}</span>}
                    {summary && <span style={{ color: 'var(--fg-muted)' }}> · {summary}</span>}
                  </div>
                  {/* 次文案：秒级时间 · 脱敏 IP · 日志事件 id（短） */}
                  <div
                    className="mono"
                    style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginTop: 2 }}
                  >
                    {auditOperationContext(e.after)
                      ? auditOperationTimeLabel(e.created_at, e.after)
                      : fmtDateTimeSec(e.created_at)}
                    {e.ip ? ` · IP ${maskIp(e.ip)}` : ''}
                    {` · ${e.id.slice(0, 8)}`}
                  </div>
                </div>
                {hasDiff && (
                  <span className="chip" style={{ fontSize: 9.5, color: 'var(--fg-muted)' }}>
                    {diff.length} 项变更
                  </span>
                )}
                {/* 失败操作（HTTP ≥400）额外红/黄状态徽标：设计稿全成功无此态，补「失败可见」（成功 2xx 不显，行内与设计 1:1） */}
                {e.status >= 400 && (
                  <span
                    className={`chip ${e.status >= 500 ? 'chip-neg' : 'chip-warn'}`}
                    style={{ fontSize: 10 }}
                  >
                    {e.status}
                  </span>
                )}
                {/* 动作类型 pill（设计稿 TYPE_TONE 单一真理源色调） */}
                <span className={`chip ${auditTypeChip(meta.type)}`} style={{ fontSize: 10 }}>
                  {meta.type}
                </span>
                <Icons.IconChevDown
                  size={13}
                  style={{
                    color: 'var(--fg-faint)',
                    transform: isOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s',
                    flexShrink: 0,
                  }}
                />
              </div>
              {/* 展开区：元数据 + 字段级 diff / 兜底文案 + 行内动作 */}
              {isOpen && (
                <div style={{ padding: '4px 14px 14px 56px', background: 'var(--bg-subtle)' }}>
                  {/* 元数据（日志 ID / 请求 ID / 操作 / 动作码 / HTTP 状态 / 来源 IP(展开区显完整供取证) / 设备·UA）*/}
                  <div
                    className="hstack"
                    style={{ gap: 18, flexWrap: 'wrap', padding: '6px 0 10px', fontSize: 11 }}
                  >
                    {(
                      [
                        ['日志 ID', e.id],
                        [anonymous ? '匿名审计组 ID' : '请求 ID', e.request_id],
                        ['操作', meta.label],
                        ['动作码', e.action],
                        ['HTTP 状态', String(e.status)],
                        ['来源 IP', e.ip ?? '—'],
                        ['设备 · UA', e.ua ?? '—'],
                      ] as const
                    ).map(([k, v]) => (
                      <div key={k}>
                        <div style={{ color: 'var(--fg-faint)', marginBottom: 2 }}>{k}</div>
                        <div className="mono" style={{ color: 'var(--fg)' }}>
                          {v}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* 字段变更 diff（统一显示规则，见 ~/lib/audit-display）*/}
                  <AuditDiffTable
                    diff={diff}
                    fieldRefs={e.field_refs}
                    action={e.action}
                    operation={auditOperationContext(e.after)}
                  />
                  {/* 行内动作：复制日志 ID / 查看该操作员（system 行不显后者）*/}
                  <div className="hstack" style={{ gap: 6, marginTop: 10 }}>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        navigator.clipboard?.writeText(e.id);
                        toast(`已复制日志 ID ${e.id}`, { tone: 'pos', icon: 'IconCopy' });
                      }}
                    >
                      <Icons.IconCopy size={11} />
                      复制日志 ID
                    </button>
                    {!isSystem && actorId !== null && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setActorFilter([actorId]);
                          setPage(1);
                        }}
                      >
                        <Icons.IconUsers size={11} />
                        查看该操作员
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {pageItems.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 12 }}>
            {unavailable ? readMessage : '暂无操作日志'}
            {(readFailed || updating) && (
              <button
                className="btn btn-sm"
                onClick={() => {
                  exportMutation.reset();
                  refetch();
                  facetsQuery.refetch();
                }}
              >
                重新加载
              </button>
            )}
          </div>
        )}
        {/* offset 分页：真实 total → totalMode="exact" 显示总数 / 总页数 + 跳任意页 */}
        {!unavailable && (
          <Pagination
            total={auditTotal}
            page={page}
            pageSize={pageSize}
            onChange={changeAuditPage}
            totalMode="exact"
          />
        )}
      </div>
    </>
  );
}
